import { useMemo } from 'react';
import type { ListingMeta } from '../../domain/models/ProductListing';
import {
  toListingMetaPresentationVm,
  toPlpBreadcrumbPresentationVm,
  toPlpStoreCountLabelPresentationVm,
  type ListingMetaPresentationVm,
  type PlpPresentationContext,
} from '../viewModels/plp.vm';
import type { PlpBreadcrumbItem } from '../../utils/plpBreadcrumb';

export interface UsePlpPresentationParams {
  listingMeta: ListingMeta | null;
  pathname: string;
  isStorePlp: boolean;
  isSearchPlp: boolean;
  storeCitySlug?: string;
  cityDisplayName?: string;
  storeAreaName?: string;
}

export interface UsePlpPresentationReturn {
  metaPresentation: ListingMetaPresentationVm | null;
  breadcrumbItems: PlpBreadcrumbItem[] | undefined;
  storeCountLabel: string;
}

export function usePlpPresentation({
  listingMeta,
  pathname,
  isStorePlp,
  isSearchPlp,
  storeCitySlug,
  cityDisplayName,
  storeAreaName,
}: UsePlpPresentationParams): UsePlpPresentationReturn {
  const ctx: PlpPresentationContext = useMemo(
    () => ({
      pathname,
      isStorePlp,
      isSearchPlp,
      storeCitySlug,
      cityDisplayName,
      storeAreaName,
    }),
    [pathname, isStorePlp, isSearchPlp, storeCitySlug, cityDisplayName, storeAreaName]
  );

  const metaPresentation = useMemo(
    () => toListingMetaPresentationVm(listingMeta, ctx),
    [listingMeta, ctx]
  );

  const breadcrumbItems = useMemo(
    () =>
      toPlpBreadcrumbPresentationVm({
        ...ctx,
        headerLabel: metaPresentation?.headerLabel,
      }),
    [ctx, metaPresentation?.headerLabel]
  );

  const storeCountLabel = useMemo(
    () =>
      toPlpStoreCountLabelPresentationVm({
        isStorePlp,
        storeAreaName,
        cityDisplayName,
      }),
    [isStorePlp, storeAreaName, cityDisplayName]
  );

  return { metaPresentation, breadcrumbItems, storeCountLabel };
}
